import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { BookOpen, ChevronRight } from 'lucide-react';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import { useMultilingualSEO } from '@/hooks/useMultilingualSEO';

const API = '';

export default function ChaptersIndex() {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const { lang: urlLang } = useParams();
  const { t, i18n } = useTranslation();
  const { getCanonicalUrl, generateHreflangTags, currentLangConfig, SITE_URL } = useMultilingualSEO('chapters');

  useEffect(() => {
    if (urlLang && ['ta', 'te', 'bn', 'kn', 'or', 'as', 'gu', 'ml', 'es', 'en', 'hi'].includes(urlLang)) {
      i18n.changeLanguage(urlLang);
    }
  }, [urlLang, i18n]);

  useEffect(() => {
    fetch(`${API}/api/public/chapters`)
      .then(r => r.json())
      .then(d => { setChapters(d.chapters || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="chapters-index-page">
      <Helmet>
        <title>{t('seo.chapters_title')}</title>
        <meta name="description" content={t('seo.chapters_desc')} />
        <link rel="canonical" href={getCanonicalUrl('/chapters')} />
        {generateHreflangTags('/chapters')}
        
        <meta property="og:title" content={t('seo.chapters_title')} />
        <meta property="og:description" content={t('seo.chapters_desc')} />
        <meta property="og:url" content={getCanonicalUrl('/chapters')} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={currentLangConfig.locale} />
        <meta property="og:image" content={`${SITE_URL}/og-logo.png`} />
        
        <meta name="twitter:title" content={t('seo.chapters_title')} />
        <meta name="twitter:description" content={t('seo.chapters_desc')} />
        
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": t('seo.chapters_title'),
          "description": t('seo.chapters_desc'),
          "inLanguage": currentLangConfig.hreflang,
          "url": getCanonicalUrl('/chapters')
        })}</script>
      </Helmet>

      {/* Nav */}
      <PublicNav />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 md:px-12 py-12 md:py-16">
        {/* Header */}
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-primary/60 mb-3 font-medium">Srimad Bhagavad Gita</p>
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground mb-4">
            {t('verse.all_chapters')} — Bhagavad Gita
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg max-w-2xl">
            700 shlokas of eternal wisdom — explore each chapter with complete Sanskrit text, Hindi and English translations, and detailed commentaries.
          </p>
        </div>

        {/* Chapter Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-3 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-testid="chapters-grid">
            {chapters.map(ch => (
              <Link
                key={ch.chapter}
                to={`${urlLang ? `/${urlLang}` : ''}/chapter/${ch.chapter}`}
                className="group bg-white/70 backdrop-blur-md border border-border/30 rounded-2xl p-6 hover:shadow-lg hover:shadow-primary/[0.06] transition-all duration-300 hover:-translate-y-0.5"
                data-testid={`chapter-card-${ch.chapter}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <span className="font-['Playfair_Display'] text-3xl font-semibold text-primary/20 group-hover:text-primary/40 transition-colors">
                    {ch.chapter}
                  </span>
                  <span className="text-xs text-muted-foreground bg-muted/50 rounded-full px-2.5 py-1">
                    {ch.verse_count} {t('verse.verses')}
                  </span>
                </div>
                <h2 className="font-['Playfair_Display'] text-lg font-medium text-foreground mb-1 group-hover:text-primary transition-colors">
                  {ch.name_en}
                </h2>
                <p className="font-['Noto_Sans_Devanagari'] text-sm text-muted-foreground mb-3">{ch.name_hi}</p>
                <div className="flex items-center text-xs text-primary/60 group-hover:text-primary transition-colors">
                  <span>Read chapter</span>
                  <ChevronRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <PublicFooter />
    </div>
  );
}
